/**
 * Simple utility to extract connection key IDs from resource data
 * This helps optimize API calls by fetching only the specific keys needed
 */

/**
 * Extracts connection key ID from a resource definition based on its type
 */
export const extractConnectionKeyFromDefinition = (
  resourceDefinition: any
): number | null => {
  if (!resourceDefinition) return null;

  // Check API definition (most common case)
  if (resourceDefinition.api_definition?.connection_key) {
    return resourceDefinition.api_definition.connection_key;
  }
  if (resourceDefinition.api_definition?.connection_key_id) {
    return resourceDefinition.api_definition.connection_key_id;
  }

  // Check SQL definition
  if (resourceDefinition.sql_definition?.connection_key) {
    return resourceDefinition.sql_definition.connection_key;
  }
  if (resourceDefinition.sql_definition?.connection_key_id) {
    return resourceDefinition.sql_definition.connection_key_id;
  }

  // Check blob definition
  if (resourceDefinition.blob_definition?.connection_key) {
    return resourceDefinition.blob_definition.connection_key;
  }
  if (resourceDefinition.blob_definition?.connection_key_id) {
    return resourceDefinition.blob_definition.connection_key_id;
  }

  // Check SFTP definition
  if (resourceDefinition.sftp_definition?.connection_key) {
    return resourceDefinition.sftp_definition.connection_key;
  }
  if (resourceDefinition.sftp_definition?.connection_key_id) {
    return resourceDefinition.sftp_definition.connection_key_id;
  }

  // Check local definition (for local type resources)
  if (resourceDefinition.local_definition?.connection_key) {
    return resourceDefinition.local_definition.connection_key;
  }
  if (resourceDefinition.local_definition?.connection_key_id) {
    return resourceDefinition.local_definition.connection_key_id;
  }

  // Check direct connection_key on resource definition
  if (resourceDefinition.connection_key) {
    return resourceDefinition.connection_key;
  }
  if (resourceDefinition.connection_key_id) {
    return resourceDefinition.connection_key_id;
  }

  return null;
};

/**
 * Extracts all unique connection key IDs needed for a resource and its related data
 * This is used to optimize API calls by fetching only specific keys instead of all keys
 */
export const extractConnectionKeyIds = (
  resourceData: any,
  additionalResources?: any[],
  linkedServicesData?: any[],
  outputStorageParams?: any,
  additionalBaseResources?: any[]
): number[] => {
  const connectionKeyIds = new Set<number>();

  // Helper function to track when connection key 146 is added
  const addConnectionKey = (id: number, source: string, data?: any) => {
    connectionKeyIds.add(id);
  };

  // Extract from main resource data
  if (resourceData) {
    // Check direct connection_key/connection_key_id property
    if (resourceData.connection_key) {
      addConnectionKey(
        resourceData.connection_key,
        "resourceData.connection_key",
        resourceData
      );
    }
    if (resourceData.connection_key_id) {
      addConnectionKey(
        resourceData.connection_key_id,
        "resourceData.connection_key_id",
        resourceData
      );
    }

    // Check linked_service_id and find corresponding connection key
    if (resourceData.linked_service_id && linkedServicesData) {
      // Find the linked service that matches this ID
      const matchingLinkedService = linkedServicesData.find(
        (ls: any) => ls.id === resourceData.linked_service_id
      );

      if (matchingLinkedService) {
        // Look for connection key in the linked service
        if (matchingLinkedService.connection_key) {
          addConnectionKey(
            matchingLinkedService.connection_key,
            "linkedService.connection_key",
            matchingLinkedService
          );
        }

        if (matchingLinkedService.connection_key_id) {
          addConnectionKey(
            matchingLinkedService.connection_key_id,
            "linkedService.connection_key_id",
            matchingLinkedService
          );
        }

        // Also check inside connection_details
        if (matchingLinkedService.connection_details) {
          // Check single connection key fields
          if (matchingLinkedService.connection_details.connection_key) {
            addConnectionKey(
              matchingLinkedService.connection_details.connection_key,
              "linkedService.connection_details.connection_key",
              matchingLinkedService.connection_details
            );
          }
          if (matchingLinkedService.connection_details.connection_key_id) {
            addConnectionKey(
              matchingLinkedService.connection_details.connection_key_id,
              "linkedService.connection_details.connection_key_id",
              matchingLinkedService.connection_details
            );
          }

          // Check connection_keys array (this is where ID 18 is stored!)
          if (
            Array.isArray(
              matchingLinkedService.connection_details.connection_keys
            )
          ) {
            matchingLinkedService.connection_details.connection_keys.forEach(
              (ckId: number) => {
                addConnectionKey(
                  ckId,
                  "linkedService.connection_details.connection_keys[]",
                  matchingLinkedService.connection_details.connection_keys
                );
              }
            );
          }
        }
      }
    }

    // Check resource definition
    const resourceDefinition =
      resourceData.additional_properties?.resource_definition;

    const mainConnectionKeyId =
      extractConnectionKeyFromDefinition(resourceDefinition);

    if (mainConnectionKeyId) {
      addConnectionKey(
        mainConnectionKeyId,
        "resourceData.resource_definition",
        resourceDefinition
      );
    }

    // Check additional_resource_data (this is in additional_properties, not at top level)
    const additionalResourceData =
      resourceData.additional_properties?.additional_resource_data;
    if (additionalResourceData && Array.isArray(additionalResourceData)) {
      additionalResourceData.forEach((additionalResource) => {
        if (additionalResource) {
          // Check direct connection_key/connection_key_id on additional resource
          if (additionalResource.connection_key) {
            connectionKeyIds.add(additionalResource.connection_key);
          }
          if (additionalResource.connection_key_id) {
            connectionKeyIds.add(additionalResource.connection_key_id);
          }

          // Check linked_service_id in additional resource and find its connection key
          if (additionalResource.linked_service_id && linkedServicesData) {
            const matchingLinkedService = linkedServicesData.find(
              (ls: any) => ls.id === additionalResource.linked_service_id
            );

            if (matchingLinkedService) {
              if (matchingLinkedService.connection_key) {
                connectionKeyIds.add(matchingLinkedService.connection_key);
              }

              if (matchingLinkedService.connection_key_id) {
                connectionKeyIds.add(matchingLinkedService.connection_key_id);
              }

              // Also check inside connection_details
              if (matchingLinkedService.connection_details) {
                // Check single connection key fields
                if (matchingLinkedService.connection_details.connection_key) {
                  connectionKeyIds.add(
                    matchingLinkedService.connection_details.connection_key
                  );
                }
                if (
                  matchingLinkedService.connection_details.connection_key_id
                ) {
                  connectionKeyIds.add(
                    matchingLinkedService.connection_details.connection_key_id
                  );
                }

                // Check connection_keys array
                if (
                  Array.isArray(
                    matchingLinkedService.connection_details.connection_keys
                  )
                ) {
                  matchingLinkedService.connection_details.connection_keys.forEach(
                    (ckId: number) => {
                      connectionKeyIds.add(ckId);
                    }
                  );
                }
              }
            }
          }

          // Check resource definition in additional resource
          const additionalResourceDefinition =
            additionalResource.additional_properties?.resource_definition;
          const additionalConnectionKeyId = extractConnectionKeyFromDefinition(
            additionalResourceDefinition
          );
          if (additionalConnectionKeyId) {
            connectionKeyIds.add(additionalConnectionKeyId);
          }
        }
      });
    }

    // CRITICAL: Check aggregation_base_resource_data for connection keys (ReRun data structure)
    if (resourceData.aggregation_base_resource_data) {
      const baseResourceData = resourceData.aggregation_base_resource_data;

      // Check direct connection_key in aggregation base resource
      if (baseResourceData.connection_key) {
        addConnectionKey(
          baseResourceData.connection_key,
          "resourceData.aggregation_base_resource_data.connection_key",
          baseResourceData
        );
      }
      if (baseResourceData.connection_key_id) {
        addConnectionKey(
          baseResourceData.connection_key_id,
          "resourceData.aggregation_base_resource_data.connection_key_id",
          baseResourceData
        );
      }
    }

    // CRITICAL: Check additional_base_resource_data array for connection keys (ReRun data structure)
    if (
      resourceData.additional_base_resource_data &&
      Array.isArray(resourceData.additional_base_resource_data)
    ) {
      resourceData.additional_base_resource_data.forEach(
        (baseResource: any, index: number) => {
          if (baseResource) {
            // Check direct connection_key in additional base resource
            if (baseResource.connection_key) {
              addConnectionKey(
                baseResource.connection_key,
                `resourceData.additional_base_resource_data[${index}].connection_key`,
                baseResource
              );
            }
            if (baseResource.connection_key_id) {
              addConnectionKey(
                baseResource.connection_key_id,
                `resourceData.additional_base_resource_data[${index}].connection_key_id`,
                baseResource
              );
            }
          }
        }
      );
    }

    // Check if we can find connection key by system_id
    if (resourceData.system_id && linkedServicesData) {
      // Look through linked services to find one that might correspond to this system
      const systemLinkedService = linkedServicesData.find((ls: any) => {
        // Check if linked service has system_id or if its ID matches the system_id
        return (
          ls.system_id === resourceData.system_id ||
          ls.id === resourceData.system_id
        );
      });

      if (systemLinkedService) {
        if (systemLinkedService.connection_key) {
          connectionKeyIds.add(systemLinkedService.connection_key);
        }

        if (systemLinkedService.connection_key_id) {
          connectionKeyIds.add(systemLinkedService.connection_key_id);
        }

        // Also check inside connection_details
        if (systemLinkedService.connection_details) {
          // Check single connection key fields
          if (systemLinkedService.connection_details.connection_key) {
            connectionKeyIds.add(
              systemLinkedService.connection_details.connection_key
            );
          }
          if (systemLinkedService.connection_details.connection_key_id) {
            connectionKeyIds.add(
              systemLinkedService.connection_details.connection_key_id
            );
          }

          // Check connection_keys array
          if (
            Array.isArray(
              systemLinkedService.connection_details.connection_keys
            )
          ) {
            systemLinkedService.connection_details.connection_keys.forEach(
              (ckId: number) => {
                connectionKeyIds.add(ckId);
              }
            );
          }
        }

        // Use the linked service ID as the connection key (common pattern)
        if (systemLinkedService.id) {
          connectionKeyIds.add(systemLinkedService.id);
        }
      }
    }
  }

  // Extract from additional resources (these are the full additional resource objects)
  if (additionalResources && Array.isArray(additionalResources)) {
    additionalResources.forEach((additionalResource) => {
      if (additionalResource) {
        // Check direct connection_key/connection_key_id property
        if (additionalResource.connection_key) {
          connectionKeyIds.add(additionalResource.connection_key);
        }
        if (additionalResource.connection_key_id) {
          connectionKeyIds.add(additionalResource.connection_key_id);
        }

        // Check linked_service_id and find corresponding connection key
        if (additionalResource.linked_service_id && linkedServicesData) {
          const matchingLinkedService = linkedServicesData.find(
            (ls: any) => ls.id === additionalResource.linked_service_id
          );
          if (matchingLinkedService) {
            if (matchingLinkedService.connection_key) {
              connectionKeyIds.add(matchingLinkedService.connection_key);
            }
            if (matchingLinkedService.connection_key_id) {
              connectionKeyIds.add(matchingLinkedService.connection_key_id);
            }

            // Also check inside connection_details
            if (matchingLinkedService.connection_details) {
              // Check single connection key fields
              if (matchingLinkedService.connection_details.connection_key) {
                connectionKeyIds.add(
                  matchingLinkedService.connection_details.connection_key
                );
              }
              if (matchingLinkedService.connection_details.connection_key_id) {
                connectionKeyIds.add(
                  matchingLinkedService.connection_details.connection_key_id
                );
              }

              // Check connection_keys array
              if (
                Array.isArray(
                  matchingLinkedService.connection_details.connection_keys
                )
              ) {
                matchingLinkedService.connection_details.connection_keys.forEach(
                  (ckId: number) => {
                    connectionKeyIds.add(ckId);
                  }
                );
              }
            }
          }
        }

        // Check resource definition in additional properties
        const additionalResourceDefinition =
          additionalResource.additional_properties?.resource_definition;
        const additionalConnectionKeyId = extractConnectionKeyFromDefinition(
          additionalResourceDefinition
        );
        if (additionalConnectionKeyId) {
          connectionKeyIds.add(additionalConnectionKeyId);
        }
      }
    });
  }

  // Extract from additional base resources (these are the full additional base resource objects)
  if (additionalBaseResources && Array.isArray(additionalBaseResources)) {
    additionalBaseResources.forEach((baseResource) => {
      if (baseResource) {
        // Check direct connection_key/connection_key_id property
        if (baseResource.connection_key) {
          connectionKeyIds.add(baseResource.connection_key);
        }
        if (baseResource.connection_key_id) {
          connectionKeyIds.add(baseResource.connection_key_id);
        }

        // Check linked_service_id and find corresponding connection key
        if (baseResource.linked_service_id && linkedServicesData) {
          const matchingLinkedService = linkedServicesData.find(
            (ls: any) => ls.id === baseResource.linked_service_id
          );
          if (matchingLinkedService) {
            if (matchingLinkedService.connection_key) {
              connectionKeyIds.add(matchingLinkedService.connection_key);
            }
            if (matchingLinkedService.connection_key_id) {
              connectionKeyIds.add(matchingLinkedService.connection_key_id);
            }

            // Also check inside connection_details
            if (matchingLinkedService.connection_details) {
              // Check single connection key fields
              if (matchingLinkedService.connection_details.connection_key) {
                connectionKeyIds.add(
                  matchingLinkedService.connection_details.connection_key
                );
              }
              if (matchingLinkedService.connection_details.connection_key_id) {
                connectionKeyIds.add(
                  matchingLinkedService.connection_details.connection_key_id
                );
              }

              // Check connection_keys array
              if (
                Array.isArray(
                  matchingLinkedService.connection_details.connection_keys
                )
              ) {
                matchingLinkedService.connection_details.connection_keys.forEach(
                  (ckId: number) => {
                    connectionKeyIds.add(ckId);
                  }
                );
              }
            }
          }
        }

        // Check resource definition in additional properties
        const baseResourceDefinition =
          baseResource.additional_properties?.resource_definition;
        const baseConnectionKeyId = extractConnectionKeyFromDefinition(
          baseResourceDefinition
        );
        if (baseConnectionKeyId) {
          connectionKeyIds.add(baseConnectionKeyId);
        }
      }
    });
  }

  // Note: We've already processed the main resource's linked service above
  // Additional linked services processing can be added here if needed

  // Extract from output storage parameters (for ReRun execution data)
  if (outputStorageParams?.output_storage_connection_key) {
    connectionKeyIds.add(outputStorageParams.output_storage_connection_key);
  }

  const finalIds = Array.from(connectionKeyIds).filter(
    (id) => id != null && id > 0
  );

  return finalIds;
};
